import { MapMarkerType, RouteTimelineDtoModelType, VisitDtoModelType } from '../../models';

type MarkerLike = MapMarkerType | MapMarkerType['properties'] | undefined;
type SelectedMarker = Pick<MapMarkerType, 'properties'>;

interface SelectedItemIds {
  unscheduledOrders: string[];
  stops: string[];
}

interface SelectedMarkerItems {
  stops?: RouteTimelineDtoModelType[];
  unscheduledOrders?: VisitDtoModelType[];
}

export const getMarkerProperties = (marker?: MarkerLike): MapMarkerType['properties'] | undefined => (
  marker && 'properties' in marker ? marker.properties : marker
);

// Map interactions receive either a single marker or a same-location cluster.
// Multi-select expands clusters to every child id; plain selection uses the first child.
export const getMarkerSelectionIds = (marker?: MarkerLike, includeClusterItems = true): string[] => {
  const properties = getMarkerProperties(marker);
  if (!properties) {
    return [];
  }

  if (properties.cluster) {
    if (includeClusterItems) {
      return properties.itemsIds ?? [];
    }

    const firstItemId = properties.itemsIds?.[0];
    return firstItemId ? [firstItemId] : [];
  }

  return properties.id ? [properties.id] : [];
};

export const hasMultiSelectModifier = (event?: any): boolean => {
  const sourceEvent = event?.srcEvent ?? event?.sourceEvent ?? event;
  return !!(sourceEvent?.shiftKey || sourceEvent?.metaKey || sourceEvent?.ctrlKey);
};

// selectedMarkers is the lightweight map-layer representation; selectedItems is
// the resolved MST representation used by panels, tables, drag, and batch actions.
export const getSelectedItemIdsFromMarkers = (markers?: MarkerLike[]): SelectedItemIds => (
  markers?.reduce((acc: SelectedItemIds, marker: MarkerLike) => {
    const markerProperties = getMarkerProperties(marker);
    const itemIds = getMarkerSelectionIds(marker);

    itemIds.forEach((itemId: string) => {
      if (markerProperties?.type === 'unscheduled') {
        acc.unscheduledOrders = [...acc.unscheduledOrders, itemId];
      } else {
        acc.stops = [...acc.stops, itemId];
      }
    });

    return acc;
  }, { unscheduledOrders: [], stops: [] }) ?? { unscheduledOrders: [], stops: [] }
);

export const getSelectedStopMarkersFromItems = (
  stops?: RouteTimelineDtoModelType[],
): SelectedMarker[] => (
  stops?.map((stop) => ({
    properties: {
      id: stop.id,
      type: 'delivering_stop',
    },
  })) ?? []
);

export const getSelectedStopIdsFromItems = (stops?: RouteTimelineDtoModelType[]): string[] => (
  stops?.map((stop) => stop.id) ?? []
);

export const getSelectedOrderMarkersFromItems = (
  orders?: VisitDtoModelType[],
): SelectedMarker[] => (
  orders?.map((order) => ({
    properties: {
      id: order.id,
      type: 'unscheduled',
    },
  })) ?? []
);

export const getSelectedOrderIdsFromItems = (orders?: VisitDtoModelType[]): string[] => (
  orders?.map((order) => order.id) ?? []
);

export const getSelectedMarkersFromItems = ({
  stops,
  unscheduledOrders,
}: SelectedMarkerItems): SelectedMarker[] => ([
  ...getSelectedStopMarkersFromItems(stops),
  ...getSelectedOrderMarkersFromItems(unscheduledOrders),
]);

// Build both outputs in one pass so marker multi-select stays linear for large selections.
const getSelectedMarkerState = ({
  stops,
  unscheduledOrders,
}: SelectedMarkerItems): { selectedMarkers: SelectedMarker[], selectedMarkerIds: Set<string> } => {
  const selectedMarkers: SelectedMarker[] = [];
  const selectedMarkerIds = new Set<string>();

  stops?.forEach((stop) => {
    selectedMarkerIds.add(stop.id);
    selectedMarkers.push({
      properties: {
        id: stop.id,
        type: 'delivering_stop',
      },
    });
  });

  unscheduledOrders?.forEach((order) => {
    selectedMarkerIds.add(order.id);
    selectedMarkers.push({
      properties: {
        id: order.id,
        type: 'unscheduled',
      },
    });
  });

  return { selectedMarkers, selectedMarkerIds };
};

const getUniqueMarkerIds = (markerIds: string[]): string[] => {
  const uniqueMarkerIds: string[] = [];
  const seenMarkerIds = new Set<string>();

  markerIds.forEach((markerId) => {
    if (markerId && !seenMarkerIds.has(markerId)) {
      uniqueMarkerIds.push(markerId);
      seenMarkerIds.add(markerId);
    }
  });

  return uniqueMarkerIds;
};

// Modifier-click treats a cluster as a group: add every unselected child, or
// remove every child when the full clicked group is already selected.
export const getNextSelectedMarkers = (
  clickedMarker: MapMarkerType['properties'],
  selectedMarkerIds: string[],
  selectedItems: SelectedMarkerItems,
): SelectedMarker[] | undefined => {
  const { selectedMarkers, selectedMarkerIds: currentSelectedMarkerIds } = getSelectedMarkerState(selectedItems);
  const clickedMarkerIds = getUniqueMarkerIds(selectedMarkerIds);
  const clickedMarkerIdSet = new Set(clickedMarkerIds);

  if (clickedMarkerIds.every((selectedMarkerId) => currentSelectedMarkerIds.has(selectedMarkerId))) {
    const remainingSelectedMarkers = selectedMarkers.filter((marker) => !clickedMarkerIdSet.has(marker.properties.id));

    return remainingSelectedMarkers.length ? remainingSelectedMarkers : undefined;
  }

  const nextSelectedMarkers = [
    ...selectedMarkers,
    ...clickedMarkerIds
      .filter((selectedMarkerId) => !currentSelectedMarkerIds.has(selectedMarkerId))
      .map((selectedMarkerId) => ({
        properties: {
          ...clickedMarker,
          id: selectedMarkerId,
          cluster: false,
          itemsIds: undefined,
        },
      })),
  ];

  return nextSelectedMarkers.length ? nextSelectedMarkers : undefined;
};
